import axios from 'axios';
import app from '@/main';
import { uniqueMap } from '../helpers';
import { sisenseFilters } from '../helpers/sisense-filters-v2';
import { ISelectOption } from '../interfaces';
import { ISisenseApiResponse, ISisenseDashboardFilters } from '../interfaces/sisense';
import { adminRequest } from '../request-configs/admin-request';

export type TFSAResult = {
  id: number,
  analyticsCityId: number,
  rentsyncCityId: number,
  fsa: string,
  city: string,
  province: string,
  allCitiesWithFSA: string,
};

/**
 * Gets default client filter JAQL format that accompanies every filtering request to Sisense.
 * @param clientId That's obvious.
 * @returns JAQL filter for clients.id
 */
export const getClientFilterJaql = (clientId: number): ISisenseDashboardFilters => ({
  jaql: {
    title: 'Client ID',
    dim: '[clients.id]',
    datatype: 'numeric',
    filter: {
      explicit: true,
      multiSelection: true,
      members: [ `${clientId}` ],
    },
  },
});

/**
 * Makes a request to Sisense for some data that exists on their end and not on the LIFT side.
 * Example: Ad Campaigns
 * @param method The Request Method (get | post)
 * @param endpointUrl Endpoint on Sisense side.
 * @param data Arbitrary data to send to Sisense for filtering. Usually in the form of Jaql.
 * @returns Response defined by generic typing - defaults to `any` if not provided.
 */
export const getDataFromSisense = async <Response = any>(
  method: 'get' | 'post',
  endpointUrl: string,
  data?: any,
  ignoreClientFilter?: boolean,
) => {
  const vm = app();

  if (!vm) {
    return null;
  }

  const url = `${vm.$env!.VUE_APP_SISENSE_URL}/${endpointUrl}`;

  if (method === 'get') {
    return (await axios.get<Response>(url, {
      withCredentials: true,
    })).data;
  }

  if (!ignoreClientFilter) {
    data.metadata.push(getClientFilterJaql(vm.$rsGlobals.$currentClient.id ?? -1).jaql);
  }

  return (await axios.post<Response>(
    url,
    data,
    {
      withCredentials: true,
    },
  )).data;
};

export const getGoogleAdCampaigns = async () => {
  try {
    const dataModelName: string = (window?.APP_CONFIG?.VUE_APP_ENV === 'demo') ? 'Google Ads - DEMO' : 'Google Ads';

    const response = await getDataFromSisense<ISisenseApiResponse>(
      'post',
      `api/datasources/${dataModelName}/jaql`,
      sisenseFilters.adCampaigns.requestDataConfig(),
    );

    if (response?.error) {
      throw new Error(`${response.database}: ${response.details}`);
    }

    return response?.values?.map((campaign: [0, '1']) => ({ id: campaign[0], name: campaign[1] })) ?? [];
  } catch (err) {
    console.error(err);

    app()?.$eventBus.$emit('show-error-toast', {
      message: 'Please refresh.',
      title: 'Error Retrieving Campaigns',
    });

    return null;
  }
};

const getPremiumReportingData = async (cityIds?: number[], useSisenseCityIds?: boolean) => {
  const response = await adminRequest.post<TFSAResult[]>('/client-api/premium-reporting-fsas/_search', {
    ...cityIds && { cityIds, useSisenseCityIds },
  });

  return response.data ?? [];
};

export const getPremiumReportingFSAs = async (cityIds?: number[], useSisenseCityIds?: boolean) => {
  const response = await getPremiumReportingData(cityIds, useSisenseCityIds);

  return response.map((item) => ({
    fsa: item.fsa,
    name: `${item.city}, ${item.province}`,
  })) ?? [];
};

export const getPremiumReportingCities = async (cityIds?: number[], useAnalyticsId?: boolean) => {
  const response = await getPremiumReportingData(cityIds);
  const valueKey: keyof TFSAResult = useAnalyticsId ? 'analyticsCityId' : 'rentsyncCityId';

  const uniqueCityIds = [ ...new Set(response.map((c) => c[valueKey])) ];

  const options = uniqueCityIds.map((id) => {
    const city = response.find((c) => c[valueKey] === id);

    if (!city) {
      return {
        value: '',
        text: '',
        subText: '',
      };
    }

    // TODO: this logic might need to be re-applied at a later date.
    // if (useAnalyticsId) {
    //   const marketCitiesForCityId = response
    //     .filter((m) => m.analyticsCityId === id)
    //     .flatMap((m) => m.allCitiesWithFSA.split(','));

    //   const uniqueCitiesInMarket = [...new Set<string>(marketCitiesForCityId) ].join(',');

    //   return {
    //     value: uniqueCitiesInMarket,
    //     text: `${city.city}, ${city.province}`,
    //     subText: `${city.rentsyncCityId}`,
    //   };
    // }

    return {
      value: `${city[valueKey]}`,
      text: `${city.city}, ${city.province}`,
      subText: `${city.rentsyncCityId}`,
    };
  });

  return options;
};

export default {
  getDataFromSisense,
  getGoogleAdCampaigns,
  getPremiumReportingFSAs,
  getPremiumReportingCities,
};
